import { defineStore } from 'pinia'
import { createPlayer } from '../models/player'
import { useContractStore } from './contract'

function defaultState() {
  return {
    view: 'start',
    players: [],
    roundNumber: 1,
    roundStarted: false,
    roundHistory: [],
    confirmingEndGame: false,
    editingRoundNumber: null
  }
}

export const useGameStore = defineStore('game', {
  state: () => {
    const saved = sessionStorage.getItem('game')
    const persisted = saved ? JSON.parse(saved) : {}
    return { ...defaultState(), ...persisted, confirmingEndGame: false }
  },

  getters: {
    playerNames: (state) => state.players.map(p => p.name),
    hasSavedGame: (state) => state.roundStarted,
    dealerId: (state) => {
      if (!state.players.length) return null
      return state.players[(state.roundNumber - 1) % state.players.length]?.id
    },
    lastRoundResult: (state) => state.roundHistory[state.roundHistory.length - 1]?.result ?? null,
    roundHistoryTable: (state) => ({
      players: state.players.map(p => ({ id: p.id, name: p.name })),
      rows: state.roundHistory.map(entry => {
        const pointsById = Object.fromEntries(entry.playerPoints.map(pp => [pp.id, pp.points]))
        return {
          roundNumber: entry.roundNumber,
          contract: entry.contract || null,
          result: entry.result || null,
          playerPoints: state.players.map(p => ({ id: p.id, points: pointsById[p.id] ?? 0 }))
        }
      })
    })
  },

  actions: {
    setView(view) {
      this.view = view
    },

    /** Zet het spel volledig terug naar het startscherm. */
    resetGame() {
      useContractStore().reset()
      this.$patch(defaultState())
    },

    /**
     * Zet een geimporteerde spelstand terug. Alleen bekende velden worden
     * overgenomen, zodat een bestand geen onbekende state kan binnenbrengen.
     */
    importState(persisted) {
      const base = defaultState()
      const next = { ...base }
      for (const key of Object.keys(base)) {
        if (key === 'confirmingEndGame') continue
        if (Object.prototype.hasOwnProperty.call(persisted, key)) next[key] = persisted[key]
      }
      useContractStore().reset()
      this.$patch(next)
    },

    startNewGame() {
      useContractStore().reset()
      this.$patch({ ...defaultState(), view: 'setup' })
    },

    restoreGame() {
      this.view = 'scoreboard'
    },

    confirmPlayerNames(names) {
      this.players = names.map((name, id) => createPlayer(id, name))
      this.view = 'dealer'
    },

    confirmDealer(dealerIndex) {
      const names = this.playerNames
      const ordered = names
        .slice(dealerIndex)
        .concat(names.slice(0, dealerIndex))
        .map((name, id) => createPlayer(id, name))
      this.players = ordered
      this.view = 'scoreboard'
    },

    startRound() {
      useContractStore().reset()
      this.view = 'round'
    },

    cancelRound() {
      useContractStore().reset()
      this.view = 'scoreboard'
    },

    startPlaying() {
      this.view = 'playing'
    },

    _applyRoundResult(roundNumber, scoreDeltas, resultData) {
      // Apply score deltas to players
      for (const player of this.players) {
        player.score += scoreDeltas[player.id] || 0
      }

      // Capture contract info for history
      const contractStore = useContractStore()
      const cs = contractStore.contractState
      const contract = cs ? {
        label: cs.label,
        type: cs.type,
        suitSymbol: cs.suitSymbol || null,
        suitRed: cs.suitRed || false,
        tricks: cs.tricks || null,
        playerIds: cs.playerIds || [],
      } : null

      // Capture result info for history
      const result = resultData ? {
        tricksTaken: resultData.tricksTaken,
        singleResult: resultData.singleResult,
        playerResults: resultData.playerResults || {},
      } : null

      // Record round history
      this.roundHistory.push({
        roundNumber,
        contract,
        result,
        playerPoints: this.players.map(p => ({
          id: p.id,
          points: scoreDeltas[p.id] || 0,
          total: p.score
        }))
      })
    },

    recordRoundResult(scoreDeltas, resultData) {
      this._applyRoundResult(this.roundNumber, scoreDeltas, resultData)
      this.roundStarted = true
      this.roundNumber++
      this.view = 'scoreboard'
    },

    startEditLastRound() {
      const last = this.roundHistory[this.roundHistory.length - 1]
      if (!last) return
      this.editingRoundNumber = last.roundNumber
      this.view = 'result'
    },

    cancelEditLastRound() {
      this.editingRoundNumber = null
      this.view = 'scoreboard'
    },

    updateLastRoundResult(scoreDeltas, resultData) {
      const last = this.roundHistory[this.roundHistory.length - 1]
      if (!last) return

      // Reverse the previous score deltas
      const pointsById = Object.fromEntries(last.playerPoints.map(pp => [pp.id, pp.points]))
      for (const player of this.players) {
        player.score -= pointsById[player.id] || 0
      }
      this.roundHistory.pop()

      this._applyRoundResult(last.roundNumber, scoreDeltas, resultData)
      this.editingRoundNumber = null
      this.view = 'scoreboard'
    },

    requestEndGame() {
      this.confirmingEndGame = true
    },

    cancelEndGame() {
      this.confirmingEndGame = false
    },

    endGame() {
      useContractStore().reset()
      this.confirmingEndGame = false
      this.view = 'start'
    }
  }
})
